import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Loader from 'react-loader-spinner';
import { FiCalendar, FiPlus, FiSearch, FiUsers, FiX } from 'react-icons/fi';
import { API_URLS, getTeamImageUrl } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { IfCanWrite, useAdminAccess } from '../utilities/adminAccess';
import { roleLabel } from '../utilities/usersApi';
import CustomModal from '../components/CustomModal';
import {
  assignPersonToMatch,
  assignmentTarget,
  assignableRoles,
  compareStaffByRoleThenName,
  compareStaffPeople,
  extractAdminStaff,
  extractAdminStaffMatches,
  groupMatchesByDateAndStadium,
  isRefereeRole,
  matchStaffSignature,
  matchToSavePayload,
  refereeSlotLabelKey,
  REFEREE_SLOT_OPTIONS,
  removePersonFromMatch,
  staffDisplayName,
  staffPhotoUrl,
  staffRequestBody,
  staffRowKey,
  teamCardName,
} from '../utilities/matchStaffApi';
import StadiumCrumbs from '../components/StadiumCrumbs';
import defaultAvatar from '../images/defaultAvatar.png';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/matchStaffAssignments.css';

const DRAG_TYPE = 'application/x-afl-staff';

const todayIso = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

export default function MatchStaffAssignments() {
  const dispatch = useDispatch();
  const { t, lang } = useI18n();
  const { champId, selected: championship, loading: champsLoading } = useChampionships();
  const { canWrite } = useAdminAccess('matches');

  const [staff, setStaff] = useState([]);
  const [matches, setMatches] = useState([]);
  const [weekFrom, setWeekFrom] = useState(todayIso());
  const [hiddenIds, setHiddenIds] = useState([]);
  const [pool, setPool] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dropMatchId, setDropMatchId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [originalById, setOriginalById] = useState(() => new Map());
  const [message, setMessage] = useState({ text: '', type: '' });
  const [refereePick, setRefereePick] = useState(null);

  const pickerRef = useRef(null);
  const requestIdRef = useRef(0);
  const draggedRef = useRef(false);

  // Как на стадии / в карточке: город и пояс чемпионата, если в матче нет venue_timezone
  const venueContext = useMemo(() => {
    if (!championship) return null;
    return {
      ...championship,
      venue_timezone: championship.venue_timezone || championship.timezone,
      city_name: championship.city || championship.city_ru || championship.name || '',
      city: championship.city || championship.city_ru || '',
      championship,
    };
  }, [championship]);

  const selectedPerson = useMemo(
    () => pool.find((item) => staffRowKey(item) === selectedKey) || null,
    [pool, selectedKey]
  );

  const visibleMatches = useMemo(
    () => matches.filter((match) => !hiddenIds.includes(match.id)),
    [matches, hiddenIds]
  );

  const groupedMatches = useMemo(
    () => groupMatchesByDateAndStadium(visibleMatches),
    [visibleMatches]
  );

  const staffByKey = useMemo(() => {
    const map = new Map();
    staff.forEach((person) => {
      const key = staffRowKey(person);
      if (key) map.set(key, person);
    });
    return map;
  }, [staff]);

  const filteredStaff = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const poolKeys = new Set(pool.map(staffRowKey));
    return staff
      .filter((person) => {
        if (poolKeys.has(staffRowKey(person))) return false;
        if (!query) return true;
        const haystack = [
          person.login,
          person.name,
          person.family,
          staffDisplayName(person),
          person.position,
          roleLabel(t, person.role),
        ].join(' ').toLowerCase();
        return haystack.includes(query);
      })
      .sort(compareStaffPeople);
  }, [staff, pool, searchQuery, t]);

  const rememberOriginals = (list) => {
    setOriginalById(new Map(list.map((match) => [match.id, matchStaffSignature(match)])));
  };

  const dirty = useMemo(
    () => matches.some((match) => matchStaffSignature(match) !== originalById.get(match.id)),
    [matches, originalById]
  );

  const loadData = useCallback(async () => {
    if (!champId) {
      setStaff([]);
      setMatches([]);
      setPool([]);
      setSelectedKey(null);
      setHiddenIds([]);
      rememberOriginals([]);
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    setMessage({ text: '', type: '' });
    const date = todayIso();

    try {
      const body = staffRequestBody(champId, { date });
      const [staffRes, matchesRes] = await Promise.all([
        authorizedPost(dispatch, API_URLS.getAdminStaff, body),
        authorizedPost(dispatch, API_URLS.getAdminStaffMatches, body),
      ]);
      if (requestId !== requestIdRef.current) return;
      const nextStaff = extractAdminStaff(staffRes.data);
      const packed = extractAdminStaffMatches(matchesRes.data, nextStaff, venueContext);
      setStaff(nextStaff);
      rememberOriginals(packed.matches);
      setMatches(packed.matches);
      setWeekFrom(packed.from || date);
      setHiddenIds([]);
      setPool([]);
      setSelectedKey(null);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setStaff([]);
      setMatches([]);
      rememberOriginals([]);
      setMessage({ text: getApiErrorMessage(error, t('msLoadError')), type: 'error' });
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [champId, dispatch, t, venueContext]);

  useEffect(() => {
    if (!champId && champsLoading) return undefined;
    loadData();
    return undefined;
  }, [champId, champsLoading, loadData]);

  useEffect(() => {
    const handleClick = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setPickerOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setRefereePick(null);
        setPickerOpen(false);
        setSelectedKey(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const personFromCatalog = (person) => staffByKey.get(staffRowKey(person)) || person;

  const addToPool = (person) => {
    const key = staffRowKey(person);
    if (!key) return;
    setPool((prev) => (prev.some((item) => staffRowKey(item) === key) ? prev : [...prev, personFromCatalog(person)]));
    setSearchQuery('');
    setPickerOpen(false);
    if (canWrite) setSelectedKey(key);
  };

  const removeFromPool = (personKey) => {
    setPool((prev) => prev.filter((item) => staffRowKey(item) !== personKey));
    if (selectedKey === personKey) setSelectedKey(null);
  };

  const toggleSelected = (personKey) => {
    if (!canWrite) return;
    if (draggedRef.current) {
      draggedRef.current = false;
      return;
    }
    setSelectedKey((prev) => (prev === personKey ? null : personKey));
  };

  const showAssignError = (reason, person) => {
    const name = staffDisplayName(person);
    const role = roleLabel(t, person.role);
    if (reason === 'already') {
      setMessage({ text: t('msAlreadyAssigned', { name }), type: 'error' });
      return;
    }
    if (reason === 'full') {
      setMessage({ text: t('msSlotsFull', { role }), type: 'error' });
      return;
    }
    if (reason === 'no-slot') {
      setMessage({ text: t('msInspectorHint'), type: 'error' });
      return;
    }
    setMessage({ text: t('msCannotAssign'), type: 'error' });
  };

  const assignToMatch = (matchId, person, preferredSlot) => {
    if (!canWrite || !person) return;
    const catalogPerson = personFromCatalog(person);
    const current = matches.find((match) => match.id === matchId);
    if (!current) return;
    const result = assignPersonToMatch(current, catalogPerson, preferredSlot);
    if (!result.ok) {
      showAssignError(result.reason, catalogPerson);
      return;
    }
    setMatches((prev) => prev.map((match) => (match.id === matchId ? result.match : match)));
    setMessage({ text: '', type: '' });
  };

  const requestAssign = (matchId, person) => {
    if (!canWrite || !person) return;
    const catalogPerson = personFromCatalog(person);
    if (isRefereeRole(catalogPerson.role)) {
      const match = matches.find((item) => item.id === matchId);
      if (!match) return;
      const target = assignmentTarget(match, catalogPerson);
      if (!target.ok) {
        showAssignError(target.reason, catalogPerson);
        return;
      }
      setRefereePick({ matchId, person: catalogPerson, slots: target.slots });
      return;
    }
    assignToMatch(matchId, catalogPerson);
  };

  const confirmRefereeSlot = (slot) => {
    if (!refereePick) return;
    const { matchId, person } = refereePick;
    setRefereePick(null);
    assignToMatch(matchId, person, slot);
  };

  const unassignFromMatch = (matchId, personId, slot) => {
    if (!canWrite) return;
    setMatches((prev) => prev.map((match) => (
      match.id === matchId ? removePersonFromMatch(match, personId, slot) : match
    )));
  };

  const hideMatch = (matchId) => {
    setHiddenIds((prev) => (prev.includes(matchId) ? prev : [...prev, matchId]));
  };

  const restoreHidden = () => setHiddenIds([]);

  const onDragStart = (event, person) => {
    if (!canWrite) return;
    const key = staffRowKey(person);
    draggedRef.current = true;
    event.dataTransfer.effectAllowed = 'copy';
    try {
      event.dataTransfer.setData(DRAG_TYPE, key);
    } catch (error) {
      // Some browsers only accept text/plain.
    }
    event.dataTransfer.setData('text/plain', key);
    setDragId(key);
    setSelectedKey(key);
  };

  const onDragEnd = () => {
    setDragId(null);
    setDropMatchId(null);
    window.setTimeout(() => { draggedRef.current = false; }, 0);
  };

  const personForDrag = (event) => {
    const raw = event.dataTransfer.getData(DRAG_TYPE) || event.dataTransfer.getData('text/plain') || dragId;
    const key = String(raw || '');
    return pool.find((item) => staffRowKey(item) === key) || staffByKey.get(key) || null;
  };

  const onDragOverMatch = (event, match) => {
    if (!canWrite) return;
    const person = selectedPerson || staffByKey.get(dragId);
    if (!person) return;
    const target = assignmentTarget(match, person);
    if (!target.ok) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setDropMatchId((prev) => (prev === match.id ? prev : match.id));
  };

  const onDropMatch = (event, match) => {
    event.preventDefault();
    const person = personForDrag(event);
    setDropMatchId(null);
    setDragId(null);
    if (person) requestAssign(match.id, person);
  };

  const saveChanges = async () => {
    if (!canWrite || !champId || saving) return;
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await authorizedPost(dispatch, API_URLS.setAdminStaffMatches, staffRequestBody(champId, {
        date: weekFrom || todayIso(),
        matches: matches.map(matchToSavePayload),
      }));
      const packed = extractAdminStaffMatches(response.data, staff, venueContext);
      const hidden = new Set(hiddenIds);
      rememberOriginals(packed.matches);
      setMatches(packed.matches);
      setWeekFrom(packed.from || weekFrom);
      setHiddenIds(packed.matches.filter((item) => hidden.has(item.id)).map((item) => item.id));
      setMessage({ text: t('msSaved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('msSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const formatDateTitle = (value) => {
    if (!value) return '';
    const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) return value;
    const date = new Date(year, month - 1, day);
    const formatted = date.toLocaleDateString(lang === 'en' ? 'en-GB' : 'ru-RU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const renderAvatar = (person, className) => (
    <img
      src={staffPhotoUrl(person) || defaultAvatar}
      alt=""
      className={className}
      onError={(event) => { event.currentTarget.src = defaultAvatar; }}
    />
  );

  const renderTeamLogo = (team) => (
    <img
      src={getTeamImageUrl(team?.image) || defaultTeamLogo}
      alt=""
      className="ms-team-logo"
      onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
    />
  );

  const renderPersonLogin = (person) => (
    person.login ? <span className="ms-person-login">{person.login}</span> : null
  );

  const renderPersonMeta = (person) => (
    <span className="ms-person-roles">
      <span className={`ms-role ms-role-${person.role}`}>{person.position || roleLabel(t, person.role)}</span>
    </span>
  );

  const assignedRoleLabel = (person) => {
    const slotKey = refereeSlotLabelKey(person.slot);
    if (slotKey) return t(slotKey);
    return person.position || roleLabel(t, person.role);
  };

  const placingPerson = selectedPerson || (dragId ? staffByKey.get(dragId) : null);

  const renderMatchCard = (match) => {
    const canPlace = canWrite && placingPerson && assignmentTarget(match, placingPerson).ok;
    const isDropTarget = dropMatchId === match.id && canPlace;
    return (
      <article
        key={match.id}
        className={`ms-match ${canPlace ? 'is-assignable' : ''} ${isDropTarget ? 'is-drop' : ''}`}
        onDragOver={(event) => onDragOverMatch(event, match)}
        onDragLeave={(event) => {
          if (event.currentTarget.contains(event.relatedTarget)) return;
          setDropMatchId((prev) => (prev === match.id ? null : prev));
        }}
        onDrop={(event) => onDropMatch(event, match)}
      >
        <button
          type="button"
          className="ms-match-remove"
          onClick={() => hideMatch(match.id)}
          aria-label={t('msRemoveMatch')}
        >
          <FiX />
        </button>

        {canPlace ? (
          <button
            type="button"
            className="ms-match-plus"
            onClick={() => requestAssign(match.id, placingPerson)}
            aria-label={t('msAssignHint', { name: staffDisplayName(placingPerson) })}
          >
            <FiPlus />
          </button>
        ) : null}

        <div className="ms-match-teams">
          <div className="ms-team">
            {renderTeamLogo(match.home_team)}
            <span title={match.home_team?.name || ''}>{teamCardName(match.home_team)}</span>
          </div>
          <span className="ms-vs">{t('msVs')}</span>
          <div className="ms-team is-guest">
            {renderTeamLogo(match.guest_team)}
            <span title={match.guest_team?.name || ''}>{teamCardName(match.guest_team)}</span>
          </div>
        </div>

        {match.league_name ? <div className="ms-match-league">{match.league_name}</div> : null}

        <div className="ms-match-staff">
          {match.staff.length ? match.staff.map((item) => {
            const person = { ...personFromCatalog(item), ...item };
            return (
            <button
              key={`${person.slot}-${person.id}`}
              type="button"
              className={`ms-assigned ms-role-${person.role} ${canWrite ? 'is-clickable' : ''}`}
              onClick={() => unassignFromMatch(match.id, person.id, person.slot)}
              disabled={!canWrite}
              title={canWrite ? t('msUnassignHint', { name: staffDisplayName(person) }) : staffDisplayName(person)}
            >
              {renderAvatar(person, 'ms-assigned-photo')}
              <span className="ms-assigned-text">
                <strong>{staffDisplayName(person)}</strong>
                <em>{assignedRoleLabel(person)}</em>
              </span>
            </button>
            );
          }) : (
            <div className="ms-staff-empty">{t('msNoStaff')}</div>
          )}
        </div>

        <div className="ms-match-time">{match.time || '—'}</div>
      </article>
    );
  };

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page ms-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[{ label: t('navMatchStaff') }]} />
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll ms-scroll">
          <div className="st-content ms-content">
            {!champId && !champsLoading ? (
              <div className="st-empty">
                <FiCalendar size={48} />
                <h3>{t('msNoChamp')}</h3>
              </div>
            ) : loading ? (
              <div className="st-loading">
                <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                <p>{t('msLoading')}</p>
              </div>
            ) : (
              <div className="ms-board">
                <section className="ms-staff-col">
                  <div className="st-block ms-staff-block">
                    <h2 className="st-block-header">{t('msStaff')}</h2>
                    <div className="ms-picker" ref={pickerRef}>
                      <div className="ms-picker-field">
                        <FiSearch className="ms-picker-icon" />
                        <input
                          type="text"
                          className="st-search-input ms-picker-input"
                          placeholder={t('msStaffSearch')}
                          value={searchQuery}
                          onChange={(event) => {
                            setSearchQuery(event.target.value);
                            setPickerOpen(true);
                          }}
                          onFocus={() => setPickerOpen(true)}
                        />
                        {searchQuery ? (
                          <button type="button" className="st-search-clear" onClick={() => setSearchQuery('')}>
                            ×
                          </button>
                        ) : null}
                      </div>
                      {pickerOpen ? (
                        <div className="ms-picker-list">
                          {filteredStaff.length ? filteredStaff.map((person) => (
                            <button
                              key={staffRowKey(person)}
                              type="button"
                              className="ms-picker-item"
                              onClick={() => addToPool(person)}
                            >
                              {renderAvatar(person, 'ms-picker-photo')}
                              <span className="ms-picker-meta">
                                <strong>{staffDisplayName(person)}</strong>
                                {renderPersonLogin(person)}
                                {renderPersonMeta(person)}
                                <em>{t('msGames', { count: person.games || 0 })}</em>
                              </span>
                            </button>
                          )) : (
                            <div className="ms-picker-empty">{t('msStaffEmpty')}</div>
                          )}
                        </div>
                      ) : null}
                    </div>

                    <div className="ms-pool">
                      <div className="ms-pool-title">{t('msPool')}</div>
                      <p className="ms-pool-hint">{canWrite ? t('msPoolHint') : t('msNeedWrite')}</p>
                      {pool.length ? (
                        <div className="ms-pool-list">
                          {[...pool].sort(compareStaffByRoleThenName).map((person) => {
                            const key = staffRowKey(person);
                            const active = selectedKey === key;
                            return (
                              <div
                                key={key}
                                className={`ms-pool-card ms-role-${person.role} ${active ? 'is-active' : ''}`}
                                draggable={canWrite}
                                onDragStart={(event) => onDragStart(event, person)}
                                onDragEnd={onDragEnd}
                                onClick={() => toggleSelected(key)}
                              >
                                <button
                                  type="button"
                                  className="ms-pool-remove"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    removeFromPool(key);
                                  }}
                                  aria-label={t('msRemoveStaff')}
                                >
                                  <FiX />
                                </button>
                                {renderAvatar(person, 'ms-pool-photo')}
                                <span className="ms-pool-meta">
                                  <strong>{staffDisplayName(person)}</strong>
                                  {renderPersonMeta(person)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="ms-pool-empty">
                          <FiUsers size={22} />
                          <span>{t('msPoolEmpty')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                <section className="ms-matches-col">
                  <div className="ms-matches-head">
                    <div>
                      <h2 className="st-block-header">{t('msMatches')}</h2>
                      <p className="ms-pool-hint">{t('msMatchesHint')}</p>
                    </div>
                    {hiddenIds.length ? (
                      <button type="button" className="st-btn st-btn-ghost" onClick={restoreHidden}>
                        {t('msRestoreAll')}
                      </button>
                    ) : null}
                  </div>

                  {placingPerson && canWrite ? (
                    <div className={`ms-place-banner ${assignableRoles(placingPerson).length ? 'is-ready' : 'is-warn'}`}>
                      {assignableRoles(placingPerson).length
                        ? t('msAssignHint', { name: staffDisplayName(placingPerson) })
                        : t('msInspectorHint')}
                    </div>
                  ) : null}

                  {groupedMatches.length ? groupedMatches.map((group) => (
                    <div key={group.date} className="ms-date-group">
                      <h3 className="ms-date-title">{formatDateTitle(group.date)}</h3>
                      {group.stadiums.map((stadiumGroup) => (
                        <div key={stadiumGroup.stadium?.key || 'none'} className="ms-stadium-group">
                          <div className="ms-stadium-title">
                            {stadiumGroup.stadium?.name || t('msNoStadium')}
                          </div>
                          {(stadiumGroup.pitches || []).map((pitchGroup) => (
                            <div
                              key={pitchGroup.pitch?.key || 'p-none'}
                              className="ms-pitch-group"
                            >
                              {pitchGroup.pitch?.name ? (
                                <div className="ms-pitch-title">{pitchGroup.pitch.name}</div>
                              ) : null}
                              <div className="ms-match-grid">
                                {pitchGroup.matches.map(renderMatchCard)}
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )) : (
                    <div className="st-empty">
                      <FiCalendar size={48} />
                      <h3>{t('msMatchesEmpty')}</h3>
                    </div>
                  )}
                </section>
              </div>
            )}
          </div>
        </div>

        <CustomModal
          styles={{ width: 420, height: 'auto', paddingLeft: 24, paddingRight: 24, paddingTop: 20, paddingBottom: 20 }}
          visible={Boolean(refereePick)}
          closeModal={() => setRefereePick(null)}
        >
          <div className="dq-picker-modal">
            <div className="customModalHeader">{t('msRefereePickTitle')}</div>
            {refereePick ? (
              <p className="ms-referee-pick-name">{staffDisplayName(refereePick.person)}</p>
            ) : null}
            <div className="ms-referee-slots">
              {(refereePick?.slots || []).map((slot) => {
                const option = REFEREE_SLOT_OPTIONS.find((item) => item.slot === slot);
                if (!option) return null;
                return (
                  <button
                    key={slot}
                    type="button"
                    className="ms-referee-slot"
                    onClick={() => confirmRefereeSlot(slot)}
                  >
                    {t(option.labelKey)}
                  </button>
                );
              })}
            </div>
            <div className="st-delete-actions">
              <button type="button" className="btn-modal btn-secondary-modal" onClick={() => setRefereePick(null)}>
                {t('cancel')}
              </button>
            </div>
          </div>
        </CustomModal>

        <div className="st-actions">
          <IfCanWrite entity="matches">
            <button
              type="button"
              className="st-btn st-btn-primary"
              onClick={saveChanges}
              disabled={loading || saving || !dirty || !champId}
            >
              {saving ? t('msLoading') : t('stSaveChanges')}
            </button>
          </IfCanWrite>
        </div>
      </div>
    </div>
  );
}
